import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocale } from '../../localization/LocaleContext';
import { FAQ_CATEGORIES } from './faqData';
import FAQCategory from './FAQCategory';
import './FAQ.css';

export default function FAQ(){
    const { t, toLocalizedPath } = useLocale();
    const [query, setQuery] = useState('');

    const normalizedQuery = query.trim().toLowerCase();

    const visibleCategories = useMemo(() => {
        if (!normalizedQuery) {
            return FAQ_CATEGORIES;
        }

        return FAQ_CATEGORIES.filter((category) => {
            const heading = t(`faq.${category.translationKey}.heading`).toLowerCase();
            if (heading.includes(normalizedQuery)) {
                return true;
            }

            return category.questionKeys.some((num) => {
                const question = t(`faq.${category.translationKey}.q${num}`).toLowerCase();
                const answer = t(`faq.${category.translationKey}.a${num}`).toLowerCase();
                return question.includes(normalizedQuery) || answer.includes(normalizedQuery);
            });
        });
    }, [normalizedQuery, t]);

    const jsonLd = useMemo(() => ({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_CATEGORIES.flatMap((category) =>
            category.questionKeys.map((num) => ({
                '@type': 'Question',
                name: t(`faq.${category.translationKey}.q${num}`),
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: t(`faq.${category.translationKey}.a${num}`)
                }
            }))
        )
    }), [t]);

    return(
        <section className="sp-90">
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>
            <div className="container">
                <div className="faq-hero">
                    <h1 className="h-1 h-bold text-center">
                        {t('faq.heading')}
                    </h1>
                    <p className="paragraph p-adjustment text-center">
                        {t('faq.subheading')}
                    </p>
                </div>

                <div className="faq-search-wrap">
                    <svg className="faq-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        className="faq-search-input"
                        placeholder={t('faq.searchPlaceholder')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label={t('faq.searchPlaceholder')}
                    />
                </div>

                {!normalizedQuery && (
                    <nav className="faq-quicknav" aria-label="FAQ categories">
                        {FAQ_CATEGORIES.map((category) => (
                            <a key={category.id} href={`#${category.id}`} className="faq-quicknav-pill">
                                <span aria-hidden="true">{category.icon}</span>
                                {t(`faq.${category.translationKey}.heading`)}
                            </a>
                        ))}
                    </nav>
                )}

                <div className="faq-categories">
                    {visibleCategories.length === 0 ? (
                        <div className="faq-no-results">
                            {t('faq.noResults')}{' '}
                            <a href={toLocalizedPath('contact')}>{t('faq.noResultsLinkText')}</a>.
                        </div>
                    ) : (
                        visibleCategories.map((category) => (
                            <FAQCategory
                                key={category.id}
                                id={category.id}
                                translationKey={category.translationKey}
                                questionKeys={category.questionKeys}
                                icon={category.icon}
                            />
                        ))
                    )}
                </div>

                <div className="faq-support-section">
                    <h2 className="faq-support-heading">{t('faq.contactUs.heading')}</h2>
                    <p className="faq-support-text">{t('faq.contactUs.text')} support@invoicepoint.app</p>
                    <div className="faq-support-actions">
                        <a href={toLocalizedPath('helpCenter')} className="faq-support-btn faq-support-btn--primary">
                            {t('faq.contactUs.helpCenterCta')}
                        </a>
                        <a href="mailto:support@invoicepoint.app" className="faq-support-btn faq-support-btn--secondary">
                            {t('faq.contactUs.emailCta')}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    )
}
